import { useEffect, useState } from "react";
import clsx from "clsx";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  fetchCustomerCrmState,
  updateNextAction,
  type CustomerCrmState,
} from "../../../helpers/api/crm";

interface Props {
  customerId: string;
}

function isoToDateInput(iso: string | null): string {
  if (!iso) return "";
  return iso.slice(0, 10);
}

function dateInputToIso(v: string): string | null {
  if (!v) return null;
  return new Date(v + "T12:00:00Z").toISOString();
}

/**
 * One active "next action" per customer — body + optional due date. Clearing
 * the body drops both fields server-side. The dashboard row picks up the
 * new value via the customers query invalidate.
 */
export function NextActionSection({ customerId }: Props) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery<CustomerCrmState>({
    queryKey: ["crm", "customers", customerId, "crm-state"],
    queryFn: () => fetchCustomerCrmState(customerId),
  });

  const [body, setBody] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (data && !dirty) {
      setBody(data.nextAction.body ?? "");
      setDueDate(isoToDateInput(data.nextAction.dueDate));
    }
  }, [data, dirty]);

  const save = useMutation({
    mutationFn: () =>
      updateNextAction(customerId, {
        body: body.trim() ? body.trim() : null,
        dueDate: dateInputToIso(dueDate),
      }),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ["crm", "customers", customerId, "crm-state"],
      });
      qc.invalidateQueries({ queryKey: ["crm", "customers"] });
      setDirty(false);
    },
  });

  const clear = useMutation({
    mutationFn: () =>
      updateNextAction(customerId, { body: null, dueDate: null }),
    onSuccess: () => {
      setBody("");
      setDueDate("");
      setDirty(false);
      qc.invalidateQueries({
        queryKey: ["crm", "customers", customerId, "crm-state"],
      });
      qc.invalidateQueries({ queryKey: ["crm", "customers"] });
    },
  });

  const overdue =
    data?.nextAction.dueDate &&
    new Date(data.nextAction.dueDate).getTime() < Date.now();

  return (
    <section className="bg-white rounded-xl border border-border-secondary shadow-sm flex flex-col">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border-secondary">
        <h2 className="text-sm font-semibold text-content-primary">
          Next Action
        </h2>
        {overdue && !dirty && (
          <span className="text-[10px] uppercase tracking-wider font-semibold text-rose-700 bg-rose-50 px-1.5 py-0.5 rounded">
            Overdue
          </span>
        )}
      </div>

      <div className="flex-1 p-4 flex flex-col">
        {isLoading ? (
          <div className="h-4 w-28 bg-gray-100 rounded animate-pulse" />
        ) : (
          <>
            <textarea
              value={body}
              onChange={(e) => {
                setBody(e.target.value);
                setDirty(true);
              }}
              rows={3}
              placeholder="What's next for this account?"
              className="w-full text-sm px-2.5 py-2 border border-border-secondary rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary resize-none flex-1"
            />

            <div className="flex items-center justify-between gap-2 mt-2">
              <input
                type="date"
                value={dueDate}
                onChange={(e) => {
                  setDueDate(e.target.value);
                  setDirty(true);
                }}
                className={clsx(
                  "text-xs px-2 py-1 border rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary",
                  overdue && !dirty
                    ? "border-rose-300 text-rose-700"
                    : "border-border-secondary",
                )}
              />
              <div className="flex items-center gap-1">
                {data?.nextAction.body && (
                  <button
                    type="button"
                    disabled={clear.isPending}
                    onClick={() => {
                      if (window.confirm("Clear this next action?"))
                        clear.mutate();
                    }}
                    className="text-[11px] px-2 py-1 text-rose-600 hover:text-rose-800 disabled:opacity-50"
                  >
                    Clear
                  </button>
                )}
                <button
                  type="button"
                  disabled={!dirty || !body.trim() || save.isPending}
                  onClick={() => save.mutate()}
                  className="text-xs px-3 py-1 bg-primary text-white rounded-md disabled:opacity-50 font-medium"
                >
                  {save.isPending ? "Saving…" : "Save"}
                </button>
              </div>
            </div>
            {save.isError && (
              <p className="text-[11px] text-rose-600 mt-1">Save failed.</p>
            )}
          </>
        )}
      </div>
    </section>
  );
}
